import React from "react";

import { SkeletonText } from "~/components/skeleton/text";
import { Truncated } from "~/components/truncated";
import { matchIdentifiable } from "~/tools/identifiable";
import { pluralize } from "~/tools/pluralize";

import type { DashboardWidgetSpecInitConfig } from "../../common/dashboard-spec";
import { useParams } from "../../generic/routing";
import { type DashboardReadFullSchema, useOptimisticUpdateDashboard } from "../../queries/dashboards";
import { DASHBOARD_PATH } from "../../routes/Routes";
import { useErrorMessage } from "../_notification-error";
import { DashboardStar } from "../dashboard-star";
import { Link, useNavigateToLinkLazy } from "../react/link";
import { notification } from "../react/notification/Notification";

import { DashboardEntry } from "./_dashboard-entry";

const pluralizeSuccessToast = pluralize({
	1: "widget has",
	more: "widgets have",
});

export interface ExistingDashboardEntryProps {
	dashboard: undefined | DashboardReadFullSchema;
	widgets: ReadonlyArray<DashboardWidgetSpecInitConfig>;
	close: VoidFunction;
}

export const ExistingDashboardEntry: React.FC<ExistingDashboardEntryProps> = ({ dashboard, widgets, close }) => {
	const navigateToLink = useNavigateToLinkLazy();
	const { displayErrorMessage } = useErrorMessage();
	const { idOrIdentifier: currentDashboardIdentifier } = useParams<typeof DASHBOARD_PATH>();

	const { mutate: updateDashboard } = useOptimisticUpdateDashboard({
		onSuccess: ({ id, name }) => {
			const destination = navigateToLink(DASHBOARD_PATH, {
				params: { idOrIdentifier: id },
				search: ["timeRange", "timestamp", "detachedFilters"],
			});

			notification.success({
				duration: 5,
				message: (
					<p>
						The {pluralizeSuccessToast(widgets.length)} been successfully added to "
						{/* it can use Router.Link here because the notification is out of Router context */}
						<Link el="a" href={`#${destination}`} theme="secondary">
							{name}
						</Link>
						" dashboard.
					</p>
				),
			});
		},

		onError: (error) => {
			displayErrorMessage({
				message: "Something went wrong while adding new widgets to a dashboard.",
				error,
			});
		},
	});

	return (
		<DashboardEntry
			interactive={dashboard != null}
			key={dashboard?.id}
			onClick={() => {
				if (dashboard != null) {
					updateDashboard({
						idOrIdentifier: dashboard.id,
						diff: {
							spec: dashboard.spec.addWidgets(widgets),
						},
					});

					close();
				}
			}}
		>
			<SkeletonText randomize when={dashboard?.name}>
				{(text) => <Truncated>{text}</Truncated>}
			</SkeletonText>

			<DashboardStar className="-my-3 ml-2" id={dashboard?.id} />

			{dashboard != null &&
				currentDashboardIdentifier != null &&
				matchIdentifiable(dashboard, currentDashboardIdentifier) && (
					<span className="ml-4 flex-1 text-right font-normal text-11 text-grey-500">CURRENT</span>
				)}
		</DashboardEntry>
	);
};
